import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import clsx from 'clsx';

interface StatusFilterDropdownProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

const statusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'completed', label: 'Categorised' },
  { value: 'pending', label: 'Uncategorised' },
  { value: 'needs_review', label: 'Needs Review' },
];

export function StatusFilterDropdown({
  value,
  onChange,
  className
}: StatusFilterDropdownProps): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false);

  const selectedLabel = statusOptions.find(opt => opt.value === value)?.label || 'All Status';

  const handleSelect = (newValue: string) => {
    onChange(newValue);
    setIsOpen(false);
  };

  return (
    <div className={clsx('relative', className)}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-primary-500 focus:border-primary-500 min-w-[180px]"
        aria-label="Filter by transaction status"
        aria-expanded={isOpen}
      >
        <span className="text-sm truncate">{selectedLabel}</span>
        <ChevronDown
          className={clsx(
            'h-4 w-4 ml-2 flex-shrink-0 transition-transform',
            isOpen && 'rotate-180'
          )}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setIsOpen(false);
            }}
            aria-hidden="true"
          />
          <div className="absolute left-0 mt-2 w-80 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden">
            {/* Status List */}
            <div className="max-h-96 overflow-y-auto">
              {statusOptions.map((option) => {
                const isSelected = value === option.value;
                return (
                  <label
                    key={option.value}
                    className={clsx(
                      'w-full px-4 py-2.5 text-sm transition-colors flex items-center justify-between cursor-pointer',
                      isSelected
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        checked={isSelected}
                        onChange={() => handleSelect(option.value)}
                        aria-label={`Select ${option.label}`}
                      />
                      <span>{option.label}</span>
                    </div>
                    {isSelected && (
                      <span className="text-xs font-semibold text-primary-700 dark:text-primary-300">
                        Selected
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

